/* eslint-disable i18next/no-literal-string */
import {Button} from '@dropbox/dig-components/buttons';
import {Spinner} from '@dropbox/dig-components/progress_indicators';
import {SystemBanner} from '@dropbox/dig-components/system_banner';
import {Text} from '@dropbox/dig-components/typography';
import {Box, Split, Stack} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {AddLine} from '@dropbox/dig-icons/assets';
import {useQuery} from '@tanstack/react-query';
import {pulseUserAtom} from 'atoms/auth';
import {type AdminHomeBanner, AdminHomeBannersService} from 'client';
import {Layout} from 'components/DSYS/Layout';
import {Title} from 'components/DSYS/Title';
import {ROUTE_PATHS} from 'constant';
import {isContentEditor} from 'helpers/utils';
import {useDocumentTitle} from 'hooks/useDocumentTitle';
import {useAtomValue} from 'jotai';
import {NotFound} from 'pages/NotFound';
import {useState} from 'react';
import {getService} from 'utilities';

import {ADMIN_HOME_BANNERS_QUERY_KEY, BannerEditorModal} from './BannerEditorModal';
import {BannerList} from './BannerList';

const PAGE_TITLE = 'Home banners';

/** The editor stays mounted; see `BannerEditorModal` for why. */
type Editor = {banner: AdminHomeBanner | null; open: boolean; session: number};

/**
 * Manage the banners shown at the top of home. Content editors only, the same
 * check the admin API makes (`is_contenteditor` on the server).
 */
export const AdminHomeBanners = () => {
  const pulseUser = useAtomValue(pulseUserAtom);
  const canManage = isContentEditor(pulseUser);
  const [editor, setEditor] = useState<Editor>({banner: null, open: false, session: 0});
  const openEditor = (banner: AdminHomeBanner | null) =>
    setEditor((prev) => ({banner, open: true, session: prev.session + 1}));
  const closeEditor = () => setEditor((prev) => ({...prev, open: false}));
  const [now] = useState(() => new Date());

  useDocumentTitle(PAGE_TITLE);

  const {data, isLoading, isError} = useQuery({
    queryKey: ADMIN_HOME_BANNERS_QUERY_KEY,
    queryFn: () =>
      getService(AdminHomeBannersService).listAdminHomeBannersApiV1AdminHomeBannersGet(),
    enabled: canManage,
  });

  if (!pulseUser) {
    return null;
  }
  if (!canManage) {
    return <NotFound />;
  }

  return (
    <Layout.Container
      breadcrumb={[
        {children: 'Admin', to: ROUTE_PATHS.ADMIN},
        {children: PAGE_TITLE, to: ROUTE_PATHS.ADMIN_HOME_BANNERS},
      ]}
    >
      <Stack gap="24">
        <Split alignY="center" gap="12">
          <Split.Item width="fill">
            <Title size={24}>{PAGE_TITLE}</Title>
          </Split.Item>
          <Split.Item>
            <Button
              variant="primary"
              withIconStart={<UIIcon src={AddLine} />}
              onClick={() => openEditor(null)}
            >
              New banner
            </Button>
          </Split.Item>
        </Split>

        {isLoading ? (
          <Box paddingTop="24">
            <Spinner size="small" aria-label="Loading banners" />
          </Box>
        ) : isError ? (
          <SystemBanner tone="alert" role="alert">
            <Text>The banners couldn&apos;t be loaded. Refresh to try again.</Text>
          </SystemBanner>
        ) : data?.length ? (
          <BannerList banners={data} now={now} onEdit={openEditor} />
        ) : (
          <Text color="subtle">No banners yet. Create one to show it at the top of home.</Text>
        )}
      </Stack>

      <BannerEditorModal
        open={editor.open}
        session={editor.session}
        banner={editor.banner}
        onClose={closeEditor}
      />
    </Layout.Container>
  );
};
